<template>
  <section class="screen-device-status" :class="{ 'has-border': option.showBorder === true, 'is-cards': option.displayMode === 'cards' }" :style="panelStyle" :aria-label="option.title || '设备在线情况'" :aria-busy="String(loading)" :data-page-binding-pending="pagePending ? 'product' : null">
    <div class="device-status-body" :class="{ 'has-extra': metrics.length > 3 }">
      <figure v-if="option.displayMode !== 'cards'" class="device-status-ring" :aria-label="ringDescription">
        <svg viewBox="0 0 180 180" role="img" :aria-label="ringDescription">
          <circle cx="90" cy="90" r="70" class="device-status-ring__track" />
          <circle v-if="stats && stats.active" cx="90" cy="90" r="70" class="device-status-ring__online" :stroke-dasharray="ringDash" transform="rotate(-90 90 90)" />
        </svg>
        <figcaption v-if="option.showRate !== false || option.showOnline !== false"><strong>{{ option.showRate === false ? metricText('online') : rateText }}</strong><span>{{ option.showRate === false ? '在线设备' : '在线率' }}</span></figcaption>
      </figure>
      <dl class="device-status-metrics" aria-live="polite" aria-atomic="true">
        <div v-for="metric in metrics" :key="metric.key" class="device-status-metric" :class="'is-' + metric.key">
          <dt><span class="device-status-dot" aria-hidden="true" />{{ metric.label }}</dt><dd>{{ metric.value }}<small v-if="metric.key !== 'rate' && stats">台</small></dd>
        </div>
      </dl>
    </div>
    <p v-if="hint" class="device-status-feedback" :class="{ 'is-error': state === 'error' }" :role="state === 'error' ? 'alert' : 'status'">{{ hint }}<button v-if="state === 'error'" type="button" data-device-status @click.stop="restart()">重连</button></p>
  </section>
</template>
<script>
import { deviceStatusStyle, deviceStatusMetrics, deviceStatusDemoStatistics } from './presentation'
import { deviceStatusQuery, DEVICE_STATUS_TOPIC } from '@/runtime/device-status-model'
import { createDeviceStatusFeed } from '@/runtime/device-status-feed'
import { queryDeviceStatus } from '@/api/device-status'
import { createRequestCancellation } from '@/api/http'
import { isStaticDataPreview, readonlyDeviceStream, sharedReadOptions } from '@/runtime/shared-view'

export default {
  name: 'avue-echart-deviceStatus', inheritAttrs: false, inject: ['contain'],
  props: { option: { type: Object, default: () => ({}) }, data: { type: Object, default: () => ({}) }, index: String, id: String, disabled: Boolean },
  data: () => ({ stats: null, state: '', error: '' }),
  computed: {
    staticMode () { return this.option.dataSource === 'static' },
    demoStatisticsInput () {
      const host = this.contain, map = this.option.demoStatisticsByProduct
      const contextId = (host.navigationContext || {}).productId
      const id = contextId || (host.menuFlag && !host.isPreview ? this.option.previewProductId || this.option.productId : '')
      if (map && typeof map === 'object' && id && Object.prototype.hasOwnProperty.call(map, id)) return map[id]
      return this.option.demoStatistics === undefined ? this.data : this.option.demoStatistics
    },
    pagePending () { if (this.staticMode) return false; const id = this.contain.menuFlag && !this.contain.isPreview ? this.option.previewProductId : (this.contain.navigationContext || {}).productId; return this.option.productScope === 'page' && (id == null || (typeof id === 'string' && !id.trim())) },
    panelStyle () { return deviceStatusStyle(this.option) },
    templateMode () { return !!(this.contain.isTemplateEditing || this.contain.isComponentTemplateEditing || this.contain.isTemplatePreview || this.contain.resourceCoverReadonly || (this.contain.obj && this.contain.obj.templatePreview)) },
    bindingKey () {
      return JSON.stringify([this.option.dataSource, this.staticMode ? [this.option.demoStatistics, this.option.demoStatisticsByProduct, this.data] : null, this.option.productScope, this.option.productId, this.option.productIds, this.option.previewProductId,
        this.contain.navigationContext && this.contain.navigationContext.productId, this.contain.menuFlag,
        this.contain.isPreview, this.contain.liveProjectPreview, this.contain.isSharedView, this.contain.isSharedDataView,
        this.contain.activePageId, this.index, this.id, this.templateMode, isStaticDataPreview(this.contain)])
    },
    loading () { return !this.stats && ['loading', 'connecting'].includes(this.state) },
    metrics () { return deviceStatusMetrics(this.stats, this.option).filter(item => this.option.displayMode === 'cards' || item.key !== 'rate') },
    rateText () { return this.stats && this.stats.active ? this.stats.onlineRate.toFixed(1) + '%' : '—' },
    ringDash () { const length = 2 * Math.PI * 70; return [length * this.stats.onlineRate / 100, length].join(' ') },
    ringDescription () { return this.stats ? '在线 ' + this.stats.online + ' 台，离线 ' + this.stats.offline + ' 台，在线率 ' + this.rateText + '。未启用和未知不计入在线率。' : '设备在线统计尚未读取' },
    hint () {
      if (this.staticMode) return this.error || '演示数据'
      if (this.pagePending) return ''
      if (this.state === 'static') return '使用后配置统计范围'
      if (this.state === 'error') return this.error || '设备统计暂未更新'
      if (this.stats && !this.stats.total) return '当前授权范围内暂无设备'
      if (!this.stats && this.loading) return '正在读取设备统计…'
      return ''
    }
  },
  watch: { bindingKey () { if (this._statusMounted) this.restart() } },
  mounted () { this._statusMounted = true; this.restart() },
  beforeDestroy () { this._statusMounted = false; this.release() },
  methods: {
    metricText (key) { const item = deviceStatusMetrics(this.stats).find(metric => metric.key === key); return item ? item.value : '—' },
    release () {
      this._statusGeneration = (this._statusGeneration || 0) + 1
      clearTimeout(this._statusRetry)
      if (this._statusFeed) this._statusFeed.close()
      if (this._statusRequest) this._statusRequest.cancel()
      this._statusFeed = null; this._statusRequest = null
    },
    restart (preserve = false) {
      this.release()
      if (!preserve) this.stats = null
      this.error = ''; this.state = ''
      if (!this._statusMounted) return
      if (this.staticMode) {
        this.stats = null
        try { this.stats = deviceStatusDemoStatistics(this.demoStatisticsInput); this.state = 'demo' }
        catch (error) { this.state = 'demo-error'; this.error = error.message }
        return
      }
      const host = this.contain
      if (this.templateMode || isStaticDataPreview(host)) { this.state = 'static'; return }
      if (this.pagePending) { this.state = 'unbound'; return }
      const generation = this._statusGeneration, key = this.bindingKey
      const current = () => this._statusMounted && generation === this._statusGeneration && key === this.bindingKey
      try {
        const query = deviceStatusQuery(this.option, host.navigationContext || {}, !!host.menuFlag && !host.isPreview)
        if (host.isSharedView) {
          if (!host.activePageId || !(this.index || this.id)) throw new Error('设备统计缺少页面或组件标识')
          Object.assign(query, { pageId: host.activePageId, componentId: this.index || this.id })
        }
        const request = this._statusRequest = createRequestCancellation()
        const options = sharedReadOptions(host, { cancelToken: request.token })
        let stream
        try { stream = readonlyDeviceStream(host, 'device-status-' + this._uid + '-' + Date.now() + '-' + generation, DEVICE_STATUS_TOPIC, query) }
        catch (error) { stream = { subscribe (next, failed) { failed(error); return { unsubscribe () {} } } } }
        this._statusFeed = createDeviceStatusFeed({ stream,
          snapshot: async () => {
            const response = await queryDeviceStatus(query, options), body = response && response.data
            if (!body || body.success === false || (body.status && body.status !== 200) || !body.result) {
              const error = new Error(body && body.message || '设备统计读取失败'); error.status = body && body.status; throw error
            }
            return body.result
          },
          changed: value => { if (current()) this.stats = value },
          status: (state, error, cause) => {
            if (!current()) return
            this.state = state; this.error = error || ''
            if (state === 'ready') { this._statusAttempts = 0; clearTimeout(this._statusRetry) }
            if (state === 'error' && ![400, 401, 403, 404].includes(cause && cause.status)) {
              clearTimeout(this._statusRetry)
              const delay = Math.min(30000, 1000 * Math.pow(2, this._statusAttempts || 0))
              this._statusAttempts = (this._statusAttempts || 0) + 1
              this._statusRetry = setTimeout(() => { if (current()) this.restart(true) }, delay)
            }
          }
        })
      } catch (error) { if ([401, 403].includes(Number(error.status))) this.stats = null; this.state = 'error'; this.error = error.message || '设备统计读取失败' }
    }
  }
}
</script>
<style scoped>
.screen-device-status { display: flex; flex-direction: column; box-sizing: border-box; width: 100%; height: 100%; min-width: 0; min-height: 0; overflow: hidden; padding: 12px; color: var(--status-color); background: var(--status-background); font-size: var(--status-font-size); font-family: inherit; }
.screen-device-status.has-border { border: 1px solid var(--status-border); border-radius: 4px; }
.device-status-body { flex: 1; min-height: 0; min-width: 0; display: flex; align-items: center; gap: 12px; }
.device-status-ring { position: relative; flex: 1 1 55%; min-width: 0; max-width: 60%; margin: 0; }
.device-status-ring svg { display: block; width: 100%; max-height: 100%; overflow: visible; }
.device-status-ring circle { fill: none; stroke-width: 16; }
.device-status-ring__track { stroke: var(--status-offline); opacity: .3; }
.device-status-ring__online { stroke: var(--status-online); }
.device-status-ring figcaption { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; pointer-events: none; }
.device-status-ring figcaption strong { font-size: var(--status-value-size); font-variant-numeric: tabular-nums; white-space: nowrap; }
.device-status-ring figcaption span { font-size: .8em; color: var(--status-muted); }
.device-status-metrics { flex: 1 1 45%; min-width: 0; margin: 0; display: grid; gap: 10px; }
.device-status-metric { min-width: 0; }
.device-status-metric dt { color: var(--status-muted); font-size: .85em; display: flex; align-items: center; gap: 6px; }
.device-status-metric dd { margin: 4px 0 0; font-size: var(--status-value-size); font-variant-numeric: tabular-nums; line-height: 1.2; overflow-wrap: anywhere; }
.device-status-metric dd small { color: var(--status-muted); font-size: .45em; margin-left: 4px; }
.screen-device-status:not(.is-cards) .has-extra .device-status-metrics { gap: 8px; }
.screen-device-status:not(.is-cards) .has-extra .device-status-metric { display: flex; align-items: baseline; justify-content: space-between; gap: 4px; }
.screen-device-status:not(.is-cards) .has-extra .device-status-metric dt { font-size: .75em; }
.screen-device-status:not(.is-cards) .has-extra .device-status-metric dd { margin: 0; font-size: calc(var(--status-value-size) * .72); }
.device-status-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--status-color); flex: 0 0 auto; }
.is-online dd { color: var(--status-online); }.is-online .device-status-dot { background: var(--status-online); }
.is-offline dd { color: var(--status-offline); }.is-offline .device-status-dot { background: var(--status-offline); }
.is-notActive .device-status-dot, .is-unknown .device-status-dot { background: var(--status-inactive); }
.device-status-feedback { flex: 0 0 auto; font-size: 12px; line-height: 1.5; margin: 6px 0 0; color: var(--status-muted); overflow-wrap: anywhere; }
.device-status-feedback.is-error { color: var(--status-inactive); }
.device-status-feedback button { font: inherit; color: inherit; background: transparent; border: 1px solid currentColor; border-radius: 3px; margin-left: 8px; padding: 2px 6px; cursor: pointer; }
.device-status-feedback button:focus-visible { outline: 2px solid var(--status-online); outline-offset: 2px; }
.is-cards .device-status-body { align-items: stretch; }
.is-cards .device-status-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); align-content: center; gap: 16px; }
.is-cards .device-status-metric { padding-left: 10px; border-left: 2px solid var(--status-border); }
@media (pointer: coarse) { .device-status-feedback button { min-height: 44px; } }
</style>
